import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { MCPUserListModal } from '../../components/MCPUserListModal';
import { createMCPColumns } from './columns';
import { fetchMCPServers } from './api';
import { MCP_TABLE_CONFIG } from './constants';

function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams
) {
  return queryKeys.mcp.dataTable(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
}

interface DataTableMCPContentProps {
  organizationId: string;
}

export function DataTableMCPContent({
  organizationId,
}: DataTableMCPContentProps): ReactElement {
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const { canViewAdminContent } = useCanViewAdminContent(organizationId);

  const columns = useMemo(
    () =>
      createMCPColumns(
        organizationId,
        canViewAdminContent ? setSelectedServer : undefined
      ),
    [organizationId, canViewAdminContent]
  );

  const searchConfig = {
    placeholder: 'Search MCP servers...',
    className: 'w-[260px]',
  };

  return (
    <>
      <DataTable<MCPServerListItem>
        id={MCP_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the MCP servers table"
        search={searchConfig}
        features={{ enableSorting: true }}
        queryFn={(params, signal) =>
          fetchMCPServers(organizationId, params, signal)
        }
        getQueryKeysFromParams={params =>
          getQueryKeysFromParams(organizationId, params)
        }
        noDataLabel="No MCP servers found."
        countScope={{ organizationId }}
        queryConfig={{
          staleTime: 30000,
          refetchOnWindowFocus: false,
        }}
      />

      {canViewAdminContent && (
        <MCPUserListModal
          organizationId={organizationId}
          serverName={selectedServer}
          onClose={() => setSelectedServer(null)}
        />
      )}
    </>
  );
}

DataTableMCPContent.displayName = 'DataTableMCPContent';

export default function DataTableMCPTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  if (!organizationId) {
    return null;
  }

  return (
    <DataTableMCPContent key={organizationId} organizationId={organizationId} />
  );
}
